import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';

export interface Package {
  id: string;
  name: string;
  description?: string;
  durationDays: number;
  price: number;
  features: string[];
  isActive: boolean;
}

export interface Subscription {
  id: string;
  packageId?: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'PENDING';
  isTrial?: boolean;
  trialStartedAt?: string | null;
  trialExpiresAt?: string | null;
  startedAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  package?: Package;
  /** True when user had a subscription that has expired (enables "renew" messaging) */
  isExpired?: boolean;
}

export const subscriptionsService = {
  async getPackages(): Promise<Package[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SUBSCRIPTIONS.PACKAGES);
      const data = response.data;
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async subscribe(packageId: string, paymentMethodId?: string) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.SUBSCRIBE, {
        packageId,
        paymentMethodId,
      });
      const body = response?.data;
      return (body && typeof body === 'object' && 'data' in body ? body.data : body) ?? null;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async changePackage(packageId: string) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PACKAGE, {
        packageId,
      });
      const body = response?.data;
      return (body && typeof body === 'object' && 'data' in body ? body.data : body) ?? null;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getCurrentSubscription(): Promise<Subscription | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SUBSCRIPTIONS.CURRENT);
      const body = response?.data;
      if (body == null) return null;
      // Handle wrapped { data: ..., isExpired } - merge isExpired into subscription
      const wrapped = typeof body === 'object' && body !== null && 'data' in body;
      const raw = wrapped ? (body as { data?: unknown; isExpired?: boolean }).data : body;
      const topLevelExpired = wrapped ? (body as { isExpired?: boolean }).isExpired : undefined;
      const sub = raw as Subscription | null | undefined;
      if (sub && typeof sub === 'object' && (sub.id || sub.status)) {
        if (topLevelExpired !== undefined && sub.isExpired === undefined) {
          return { ...sub, isExpired: topLevelExpired };
        }
        return sub;
      }
      return sub ?? null;
    } catch (error: any) {
      // If 404, user has no subscription - return null instead of error
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(handleApiError(error));
    }
  },

  async activateFreeTrial() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.ACTIVATE_TRIAL);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async cancelSubscription() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.CANCEL);
      const body = response?.data;
      return (body && typeof body === 'object' && 'data' in body ? body.data : body) ?? null;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

